import React, { useState } from "react";
import { RecurringGameGroup } from "@/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronDown, ChevronRight, MoreVertical, Repeat, Settings, StopCircle, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import EditRecurringSheet from "./EditRecurringSheet";

interface RecurringGameCardProps {
  group: RecurringGameGroup;
  courseName: string;
  onRefresh?: () => void;
}

const RecurringGameCard: React.FC<RecurringGameCardProps> = ({ group, courseName, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isStopDialogOpen, setIsStopDialogOpen] = useState(false);
  const [isStopLoading, setIsStopLoading] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const isLoneWolf = group.instances[0]?.isLoneWolf ?? false;

  const handleInstanceClick = (gameId: string) => {
    navigate(`/games/${gameId}`);
  };

  // Click the card header to go to current year instance (or most recent)
  const primaryInstance = group.currentYearInstance || group.instances[0];

  const handleStopRecurring = async () => {
    setIsStopLoading(true);

    try {
      const { error } = await supabase
        .from("games")
        .update({ is_recurring: false })
        .eq("recurring_group_id", group.recurringGroupId);

      if (error) throw error;

      setIsStopDialogOpen(false);
      toast({
        title: "Recurring Stopped",
        description: "No new yearly instances will be created for this game.",
      });
      onRefresh?.();
    } catch (error) {
      console.error("Error stopping recurring game:", error);
      toast({
        title: "Error",
        description: "Failed to stop recurring. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsStopLoading(false);
    }
  };

  return (
    <>
      <Card className="hover:shadow-md transition-all">
        <CardHeader className="pb-2">
          <CardTitle className="font-display text-lg font-semibold flex items-center justify-between">
            <span
              className="cursor-pointer hover:underline"
              onClick={() => handleInstanceClick(primaryInstance.id)}
            >
              {group.name}
            </span>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-forest border-forest">
                {isLoneWolf ? (
                  <><User className="h-3 w-3 mr-1" />Lone Wolf</>
                ) : (
                  <><Repeat className="h-3 w-3 mr-1" />Recurring</>
                )}
              </Badge>
              {primaryInstance && <Badge>{primaryInstance.year}</Badge>}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsSheetOpen(true)} className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Edit Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setIsStopDialogOpen(true)}
                    className="cursor-pointer text-red-600 focus:text-red-600"
                  >
                    <StopCircle className="mr-2 h-4 w-4" />
                    Stop Recurring
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardTitle>
          <p className="text-sm text-muted-foreground">{courseName}</p>
        </CardHeader>

        <CardContent className="pt-0">
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger className="flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors w-full">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 mr-1" />
              ) : (
                <ChevronRight className="h-4 w-4 mr-1" />
              )}
              {group.instances.length} year{group.instances.length !== 1 ? 's' : ''}
            </CollapsibleTrigger>

            <CollapsibleContent className="mt-2 space-y-1">
              {group.instances.map((instance) => (
                <button
                  key={instance.id}
                  onClick={() => handleInstanceClick(instance.id)}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
                    "hover:bg-forest/5",
                    instance.year === currentYear
                      ? "bg-forest/10 font-medium"
                      : "text-muted-foreground"
                  )}
                >
                  <span>{instance.name}</span>
                  {instance.isArchived && (
                    <Badge variant="outline" className="ml-2 text-xs text-muted-foreground border-border">
                      Completed
                    </Badge>
                  )}
                </button>
              ))}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Edit Recurring Settings Sheet */}
      <EditRecurringSheet
        group={group}
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        onSuccess={() => onRefresh?.()}
      />

      {/* Stop Recurring Confirmation Dialog */}
      <AlertDialog open={isStopDialogOpen} onOpenChange={setIsStopDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop Recurring</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to stop this recurring game? No new yearly instances will be
              created. Existing instances will remain unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isStopLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStopRecurring}
              className="bg-red-600 hover:bg-red-700"
              disabled={isStopLoading}
            >
              {isStopLoading ? "Stopping..." : "Stop Recurring"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default RecurringGameCard;
